import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useSelector, useStore } from "react-redux";

import { Checkbox, FormControlLabel, FormGroup, Slide } from "@mui/material";

import { InputField } from "../components/InputField.tsx";

import { setClient } from "../Store/roiStore.ts";
import { PAR_SOLUTIONS } from "../constants.ts";
import "../styles/pages/client-details.scss";

const PAR360_PLUS = "PAR 360 Plus";
const PAR360_PREMIUM = "PAR 360 Premium";


export const ClientDetails = () => {
  const client = useSelector((state) => state.inputFields.client);
  const { dispatch } = useStore();
  const [isNameValid, setIsNameValid] = useState(true);
  const firstUpdate = useRef(true);

  const updateClient = (updates) => {
    dispatch(
      setClient({
        ...client,
        ...updates,
      })
    );
  };

  const handleSolutionChange = (solution) => {
    const selectedSolutions = client?.currentParSolutions ?? [];
    const isSelected = selectedSolutions.includes(solution);
    let nextSelections = isSelected
      ? selectedSolutions.filter((item) => item !== solution)
      : [...selectedSolutions, solution];

    // PAR 360 Premium is a superset of PAR 360 Plus: selecting Premium forces Plus
    // on as well (and the Plus checkbox is locked while Premium stays selected).
    if (solution === PAR360_PREMIUM && !isSelected && !nextSelections.includes(PAR360_PLUS)) {
      nextSelections = [...nextSelections, PAR360_PLUS];
    }

    updateClient({
      currentParSolutions: nextSelections,
    });
  };

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    setIsNameValid(client?.name.length > 0);
  }, [client?.name]);

  return (
    <Slide direction="right" in={true} mountOnEnter unmountOnExit>
      <div className="client-details-page">
        <h1>Client Details</h1>
        <p className="client-details-instructions">
          This ROI analysis will include hospital facilities where selected PAR solutions will be deployed. For each facility, you will be asked to enter baseline data used by the tool to estimate savings and ROI.
        </p>
        <div className="client-details-form">
            <InputField
              placeholder="Client name, eg.: Northern Light Healthcare"
              label="Client Name (IDN)"
              value={client?.name}
              error={!isNameValid}
              onChange={(event) => updateClient({ name: event.target.value })}
            />
            <InputField
              placeholder="Enter annual NPSR"
              label="Client's Annual Net Patient Services Revenue (All facilities combined):"
              value={client?.annualNetPatientServiceRevenue ?? 0}
              type="number"
              prefix="$"
              onValueInputChange={(name, value) =>
                updateClient({ annualNetPatientServiceRevenue: Number(value ?? 0) })
              }
            />

            <div className="client-details-section">
              <div className="client-details-section-title">
                What PAR solutions are included in this ROI analysis?
              </div>
              <FormGroup>
                {PAR_SOLUTIONS.map((solution) => {
                  const selected = client?.currentParSolutions ?? [];
                  const premiumSelected = selected.includes(PAR360_PREMIUM);
                  const lockPlus = solution === PAR360_PLUS && premiumSelected;
                  return (
                    <React.Fragment key={solution}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selected.includes(solution) || lockPlus}
                            disabled={lockPlus}
                            onChange={() => handleSolutionChange(solution)}
                          />
                        }
                        label={
                          lockPlus ? `${solution} (included with PAR 360 Premium)` : solution
                        }
                      />
                      {solution === PAR360_PREMIUM && (
                        <div className="client-details-solution-note">
                          Enables the Package Delivery freight cost savings
                          calculation on the Investment step (includes PAR 360 Plus).
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </FormGroup>
            </div>
        </div>
      </div>
    </Slide>
  );
};
